import { useEffect, useState } from "react";

import type {
  ColumnOrderState,
  ColumnPinningState,
  ColumnSizingState,
  ColumnVisibilityState,
  PaginationState,
  SortingState,
} from "@tanstack/react-table-v9";
import { useLocation } from "react-router";

export interface PersistedTableState {
  pagination?: PaginationState;
  sorting?: SortingState;
  columnVisibility?: ColumnVisibilityState;
  columnOrder?: ColumnOrderState;
  columnPinning?: ColumnPinningState;
  columnSizing?: ColumnSizingState;
  scrollY?: number;
  view?: "table" | "pivot";
  savedView?: string;
  path?: string;
  entry?: string;
  left?: boolean;
}

const STORAGE_PREFIX = "tableV2:";
const PIVOT_PREFIX = "pivotV2:";

export const currentHistoryEntry = () =>
  (window.history.state as { key?: string } | null)?.key;

export const readPersistedTableState = (
  stateKey?: string,
): PersistedTableState | undefined => {
  if (!stateKey) return undefined;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_PREFIX + stateKey);
    return raw ? (JSON.parse(raw) as PersistedTableState) : undefined;
  } catch {
    return undefined;
  }
};

export const writePersistedTableState = (
  stateKey: string,
  state: PersistedTableState,
) => {
  try {
    const existing = readPersistedTableState(stateKey) ?? {};
    // Drop search text and expanded rows saved by older versions; they can contain PHI
    const {
      globalFilter: _search,
      expanded: _expanded,
      ...layout
    } = {
      ...existing,
      ...state,
    } as PersistedTableState & { globalFilter?: unknown; expanded?: unknown };
    window.sessionStorage.setItem(
      STORAGE_PREFIX + stateKey,
      JSON.stringify(layout),
    );
  } catch {
    // Storage can be unavailable - the table still works without it
  }
};

const withinPath = (scope: string, pathname: string) => {
  const trim = (path: string) => path.replace(/\/+$/, "") || "/";
  const [a, b] = [trim(scope), trim(pathname)];
  return a === b || b.startsWith(`${a}/`) || a.startsWith(`${b}/`);
};

const storageKeys = () =>
  Array.from({ length: window.sessionStorage.length }, (_, i) =>
    window.sessionStorage.key(i),
  ).filter((key): key is string => !!key);

/** Removes a table's saved state, including its pivot's */
const removeTableState = (stateKey: string) =>
  storageKeys().forEach((key) => {
    if (
      key === STORAGE_PREFIX + stateKey ||
      key.startsWith(`${STORAGE_PREFIX}${stateKey}:`) ||
      key.startsWith(`${PIVOT_PREFIX}${stateKey}:`)
    ) {
      window.sessionStorage.removeItem(key);
    }
  });

const isRestorable = (state: PersistedTableState, pathname: string) => {
  if (state.path && !withinPath(state.path, pathname)) return false;
  if (state.left) return !!state.entry && state.entry === currentHistoryEntry();
  return true;
};

/**
 * Saved state for a table mounting on `pathname`: only from the same page or its subpages, and once the user has
 * left that page, only when Back or Forward returns to the history entry it was saved from. Anything else is removed.
 */
export const readRestorableTableState = (
  stateKey: string | undefined,
  pathname: string,
) => {
  const saved = readPersistedTableState(stateKey);
  if (!stateKey || !saved || isRestorable(saved, pathname)) return saved;
  try {
    removeTableState(stateKey);
  } catch {
    // Storage can be unavailable
  }
  return undefined;
};

/** Marks saved table state as left when the user navigates outside the page a table lives on (or its subpages) */
export const markTableStateOutside = (pathname: string) => {
  try {
    storageKeys().forEach((key) => {
      if (!key.startsWith(STORAGE_PREFIX)) return;
      const stateKey = key.slice(STORAGE_PREFIX.length);
      const saved = readPersistedTableState(stateKey);
      if (saved?.path && !saved.left && !withinPath(saved.path, pathname)) {
        writePersistedTableState(stateKey, { left: true });
      }
    });
  } catch {
    // Storage can be unavailable
  }
};

export const useTableStateCleanup = () => {
  const { pathname } = useLocation();
  useEffect(() => markTableStateOutside(pathname), [pathname]);
};

/** Reads saved state once, so later writes don't change the table's initial state */
export const usePersistedTableState = (stateKey?: string) => {
  const [initial] = useState(() =>
    readRestorableTableState(stateKey, window.location.pathname),
  );
  return initial;
};

/**
 * Saves the page scroll position on unmount and restores it once the table has rows again.
 * `savedScrollY` is read at mount so a StrictMode remount can't overwrite it first.
 */
export const useRestoreScroll = (
  stateKey: string | undefined,
  ready: boolean,
  savedScrollY: number | undefined,
) => {
  const [restored, setRestored] = useState(false);

  useEffect(() => {
    if (!stateKey || restored || !ready) return;
    if (savedScrollY) window.scrollTo({ top: savedScrollY });
    setRestored(true);
  }, [stateKey, ready, restored, savedScrollY]);

  useEffect(() => {
    if (!stateKey) return undefined;
    return () =>
      writePersistedTableState(stateKey, { scrollY: window.scrollY });
  }, [stateKey]);
};
